import React, { useEffect, useState } from "react";
import { CourseGroupApi, coursegroupsApi } from "api";
import { routes } from "consts";
import { PageMain, AdminResourceDeleteModal } from "components/molecules";
import { IDropdownOption, PaziaButton, PaziaLoader, MoreActionsMenu } from "components/atoms";
import { TanTable, TanTableColumn, useFilters, useInitFilters, usePagination, useSorting } from "components/molecules";
import { useCourseGroups } from "api/hooks";
import { useCategoriesOptions } from "api/categories";
import { useNavigate } from "react-router-dom";
import { useDeleteResourceActions } from "utils/store";
import { PageRichHeader } from "components/molecules/PageHeader/PageRichHeader";
import { CategoriesChips } from "components/molecules/CategoriesChips";
import { BulkAssignAttributesModal } from "./BulkAssignAttributesModal";
import { BulkUnassignAttributesModal } from "./BulkUnassignAttributesModal";
import { BulkAssignCategoryModal } from "./BulkAssignCategoryModal";

const FILTER_NAME = "course-groups-filter";
export const CourseGroupsPage = () => {
  const { getInitFilterValue, getInitFilterValues } = useInitFilters(FILTER_NAME);
  const { loading: loadingCategories, options: categoryOptions } = useCategoriesOptions();

  if (loadingCategories) return <PaziaLoader position="center" />;

  const categoryIds = getInitFilterValues("categories");

  return (
    <CourseGroupsPageInner
      initValues={{
        categories: categoryIds[0] ?? -1,
        categoryIds,
        name: getInitFilterValue("name", "like") ?? "",
        id: getInitFilterValue("id", "like") ?? "",
      }}
      categoryOptions={categoryOptions}
    />
  );
};

interface CourseGroupsPageInnerProps {
  initValues: {
    categories: number;
    categoryIds: number[];
    id: string;
    name: string;
  };
  categoryOptions: IDropdownOption[];
}
export const CourseGroupsPageInner = ({ initValues, categoryOptions }: CourseGroupsPageInnerProps) => {
  const { changeFilter, filter, updateAllFilters } = useFilters(FILTER_NAME);
  const { setDeleteResource } = useDeleteResourceActions();
  const { limit, onPaginationChange, skip, pagination } = usePagination();
  const { sorting, onSortingChange, fieldWithOrder } = useSorting();
  const [selectedCategory, setSelectedCategory] = useState<number>(initValues.categories);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>(initValues.categoryIds);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [bulkAssignModalOpen, setBulkAssignModalOpen] = useState(false);
  const [bulkUnassignModalOpen, setBulkUnassignModalOpen] = useState(false);
  const [bulkAssignCategoryModalOpen, setBulkAssignCategoryModalOpen] = useState(false);
  const [tableKey, setTableKey] = useState(0);
  const navigate = useNavigate();

  const { isFetching, isLoading, courseGroups, invalidate, total } = useCourseGroups(
    skip,
    limit,
    filter,
    fieldWithOrder,
  );

  useEffect(() => {
    invalidate();
  }, [sorting, pagination, filter]);

  const handleBulkSuccess = () => {
    setSelectedRows([]);
    setTableKey((prev) => prev + 1);
    invalidate();
  };

  const columns = React.useMemo<TanTableColumn<CourseGroupApi>[]>(
    () => [
      {
        id: "id",
        accessorKey: "id",
        header: "ID",
        enableColumnFilter: true,
        size: 80,
        cell: (info) => info.getValue(),
        meta: {
          filterVariant: "id",
        },
      },
      {
        id: "name",
        accessorKey: "name",
        header: "Nazwa",
        // @ts-ignore todo
        size: "aut0",
        enableColumnFilter: true,
      },
      {
        id: "category",
        header: "Kategoria",
        size: 200,
        enableColumnFilter: false,
        cell: (info: { row: { original: CourseGroupApi } }) => {
          return info.row.original.category?.name ?? "";
        },
      },
    ],
    [],
  );
  if (!courseGroups) return null;
  return (
    <PageMain>
      <PageRichHeader header="Grupy kursów">
        <div className="flex grow flex-wrap justify-between gap-2">
          <div className="flex gap-2">
            <PaziaButton type="primary" title="Dodaj +" onClick={() => navigate(routes.COURSE_GROUPS_ADD)} />
            <MoreActionsMenu
              actions={[
                { name: "Przypisz atrybuty", onClick: () => setBulkAssignModalOpen(true) },
                { name: "Odlacz atrybuty", onClick: () => setBulkUnassignModalOpen(true) },
                { name: "Przypisz kategorie", onClick: () => setBulkAssignCategoryModalOpen(true) },
              ]}
              disabled={selectedRows.length < 1}
            >
              {`Akcje (${selectedRows.length})`}
            </MoreActionsMenu>
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-4">
            <PaziaButton type="secondary" title="Obrazki" onClick={() => navigate(routes.COURSE_GROUPS_IMAGES)} />
            <PaziaButton type="secondary" title="Atrybuty" onClick={() => navigate(routes.COURSE_GROUPS_ATTRIBUTES)} />
            <PaziaButton type="secondary" title="Kategorie" onClick={() => navigate(routes.COURSES_CATEGORIES)} />
          </div>
        </div>
      </PageRichHeader>

      <div className="flex flex-col gap-4 md:flex-row">
        <div className="md:shrink-0">
          <CategoriesChips
            onSelect={(id, allIds) => {
              changeFilter("categories", id === -1 ? "" : `categories in (${allIds.join(",")})`);
              setSelectedCategory(id);
              setSelectedCategoryIds(allIds);
            }}
            selected={selectedCategory}
            pinStorageKey="course-groups-pinned-categories"
          />
        </div>

        <div className="min-w-0 flex-1">
          <TanTable
            key={tableKey}
            data={courseGroups}
            columns={columns}
            total={total}
            loading={isLoading}
            fetching={isFetching}
            onPaginationChange={onPaginationChange}
            onSortingChange={onSortingChange}
            pagination={pagination}
            onRowClick={(r) => navigate(routes.COURSE_GROUPS_EDIT_COURSES(r.id))}
            sorting={sorting}
            onClearFilters={() => {
              setSelectedCategory(-1);
              setSelectedCategoryIds([]);
              updateAllFilters([]);
            }}
            onDelete={(x) => setDeleteResource(x.id)}
            onSelectionChange={setSelectedRows}
            onFiltersChange={(filters) => {
              const catFilter =
                selectedCategory === -1
                  ? { id: "categories", value: "", apiFilter: "" }
                  : {
                      id: "categories",
                      value: selectedCategoryIds.join(","),
                      apiFilter: `categories in (${selectedCategoryIds.join(",")})`,
                    };
              updateAllFilters([...filters, catFilter].map((x) => ({ key: x.id, value: x.apiFilter })));
            }}
            initialFilters={[
              {
                id: "name",
                value: initValues.name,
              },
              {
                id: "id",
                value: initValues.id,
              },
            ]}
          />
        </div>
      </div>
      <AdminResourceDeleteModal
        title="Usuń grupę kursu"
        description="Jesteś pewien, że chcesz usunąć grupę kursu? Grupy, która ma kursy, nie da się usunąć — najpierw usuń lub przenieś jej kursy."
        onSuccessDelete={invalidate}
        deleteAction={coursegroupsApi.delete}
      />
      <BulkAssignAttributesModal
        isOpen={bulkAssignModalOpen}
        onCloseRequest={() => setBulkAssignModalOpen(false)}
        selectedCourseGroupIds={selectedRows}
        onSuccess={handleBulkSuccess}
      />
      <BulkUnassignAttributesModal
        isOpen={bulkUnassignModalOpen}
        onCloseRequest={() => setBulkUnassignModalOpen(false)}
        selectedCourseGroupIds={selectedRows}
        onSuccess={handleBulkSuccess}
      />
      <BulkAssignCategoryModal
        isOpen={bulkAssignCategoryModalOpen}
        onCloseRequest={() => setBulkAssignCategoryModalOpen(false)}
        selectedCourseGroupIds={selectedRows}
        onSuccess={handleBulkSuccess}
        categoryOptions={categoryOptions}
      />
    </PageMain>
  );
};
